/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {number} lat1 - Starting latitude
 * @param {number} lon1 - Starting longitude
 * @param {number} lat2 - Ending latitude
 * @param {number} lon2 - Ending longitude
 * @returns {number} Distance in kilometers
 */
export const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Convert kilometers to minutes (assuming avg 20 km/h city driving)
 * @param {number} distanceKm - Distance in kilometers
 * @returns {number} Estimated driving time in minutes
 */
export const kmToMinutes = (distanceKm) => {
  const avgSpeed = 20; // km/h
  return Math.ceil((distanceKm / avgSpeed) * 60);
};

/**
 * Sort stores by distance from a location
 * @param {Array} stores - Array of store objects
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @returns {Array} Stores sorted by distance (nearest first)
 */
export const sortStoresByDistance = (stores, userLat, userLng) => {
  return [...stores].sort((a, b) => {
    const distA = haversineDistance(userLat, userLng, a.lat, a.lng);
    const distB = haversineDistance(userLat, userLng, b.lat, b.lng);
    return distA - distB;
  });
};

/**
 * Filter stores within a radius
 * @param {Array} stores - Array of store objects
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @param {number} radiusKm - Search radius in kilometers
 * @returns {Array} Stores within radius
 */
export const filterStoresByRadius = (stores, userLat, userLng, radiusKm) => {
  return stores.filter(store => {
    const distance = haversineDistance(userLat, userLng, store.lat, store.lng);
    return distance <= radiusKm;
  });
};

/**
 * Add distance info to stores array
 * @param {Array} stores - Array of store objects
 * @param {number} userLat - User's latitude
 * @param {number} userLng - User's longitude
 * @returns {Array} Stores with distance_km and distance_minutes added
 */
export const enrichStoresWithDistance = (stores, userLat, userLng) => {
  return stores.map(store => ({
    ...store,
    distance_km: haversineDistance(userLat, userLng, store.lat, store.lng),
    distance_minutes: kmToMinutes(haversineDistance(userLat, userLng, store.lat, store.lng)),
  }));
};
